import React, {useState} from 'react';
import {Box, Chip, IconButton, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import {ActeBlock, Eyebrow, PhotoTile} from '../primitives.js';
import {BLOC, NATURE_TOKENS, PROBLEME_TOKENS} from '../interventionRules.js';

/**
 * Bloc CHECKLIST (ex. Maintenance préventive) — R12.
 * Zones PROBLÈME en encart rouge : une tuile photo par zone, sur la grille 2 colonnes des tuiles
 * avant / après (même taille), le libellé porté par l'étiquette de la tuile ; zones OK en chips
 * grises ; tout OK → replié. (JD 22/09 : la photo est l'information, pas le libellé.)
 */
export const ChecklistBlock = ({numero, bloc, assetsUrl, onOpen}) => {
    const tokens = NATURE_TOKENS[BLOC.CHECKLIST];
    const {summary} = bloc;
    const [expanded, setExpanded] = useState(summary.hasProbleme);

    const valeur = (
        <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
            {summary.hasProbleme ? <WarningAmberIcon sx={{fontSize: 18}}/> : <CheckIcon sx={{fontSize: 18}}/>}
            {bloc.valeur}
        </Box>
    );

    const oksChips = (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
            {summary.oks.map((e, i) => (
                <Chip key={e.id ?? i} size="small" label={e.libelle} sx={{bgcolor: '#EEF1F4', color: '#5A6470', fontWeight: 600}}/>
            ))}
        </Box>
    );

    return (
        <ActeBlock
            numero={numero} titre={bloc.titre} picto={bloc.picto} tokens={tokens}
            valeur={valeur} valeurColor={summary.hasProbleme ? PROBLEME_TOKENS.ink : tokens.ink}
            headerAction={!summary.hasProbleme && (
                <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? 'Replier' : 'Déplier'}
                            sx={{ml: 0.5, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s'}}>
                    <ExpandMoreIcon fontSize="small"/>
                </IconButton>
            )}
        >
            {summary.hasProbleme ? (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Box sx={{p: 2, borderRadius: '12px', bgcolor: PROBLEME_TOKENS.bg, border: `1px solid ${PROBLEME_TOKENS.border}`}}>
                        <Eyebrow sx={{mb: 1.5, color: PROBLEME_TOKENS.ink}}>
                            {summary.problemes.length} zone{summary.problemes.length > 1 ? 's' : ''} en problème
                        </Eyebrow>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            {summary.problemes.map((e, i) => (
                                <PhotoTile key={e.id ?? i} photos={e.photos ?? []} label={e.libelle} color={PROBLEME_TOKENS.ink}
                                           assetsUrl={assetsUrl} onOpen={onOpen}/>
                            ))}
                        </Box>
                    </Box>
                    {summary.oks.length > 0 && (
                        <Box>
                            <Eyebrow sx={{mb: 1}}>{summary.oks.length} zone{summary.oks.length > 1 ? 's' : ''} OK</Eyebrow>
                            {oksChips}
                        </Box>
                    )}
                </Box>
            ) : (expanded ? oksChips : null)}
        </ActeBlock>
    );
};

/** Bloc BOOLEEN (ex. Nettoyage graffiti) — R13. Oui → tuile photo à la taille avant / après + « Constaté / Graffiti retiré » ; sinon header seul. */
export const BooleenBlock = ({numero, bloc, assetsUrl, onOpen}) => {
    const tokens = NATURE_TOKENS[BLOC.BOOLEEN];
    const {summary, acte} = bloc;
    return (
        <ActeBlock numero={numero} titre={bloc.titre} picto={bloc.picto} tokens={tokens} valeur={bloc.valeur}>
            {summary.show ? (
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                    <PhotoTile photos={acte.photos ?? []} label={bloc.titre} color={tokens.ink} assetsUrl={assetsUrl} onOpen={onOpen}/>
                    <Box>
                        <Eyebrow sx={{mb: 1}}>Constaté</Eyebrow>
                        <Typography sx={{fontSize: 20, fontWeight: 800, color: '#2D333B'}}>Graffiti retiré</Typography>
                    </Box>
                </Box>
            ) : null}
        </ActeBlock>
    );
};
